import BaseChat from "@/shared/components/ui/ChatBot/BaseChat";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useMainStore } from "@/stores";
import { useChatController } from "@/shared/hooks/useChatController";
import ChatMessage from "@/shared/components/ui/ChatBot/ChatMessage";
import DeleteConfirmationModal from "@/shared/components/ui/Modal/DeleteConfirmationModal";
import CustomModal from "@/shared/components/ui/Modal/CustomModal";
import CommonInput from "@/shared/components/ui/Input/CommonInput";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import { Markdown } from "@/shared/components/ui/PromptKit/MessageContent";
import PromptInput from "@/shared/components/ui/PromptKit/PromptInput";
import CommonDropdownMenu from "@/shared/components/ui/DropDown/DropdownMenu";
import { Tooltip } from "@heroui/react";
import {
  ArrowRightToLine,
  Check,
  EllipsisVertical,
  Loader2,
  MessageSquare,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import {
  useAITrainerThreads,
  useCreateAITrainerThread,
  useDeleteAITrainerMemory,
  useDeleteAITrainerThread,
  useFetchAITrainerChatHistory,
  useFetchAITrainerMemories,
  useGetAITrainerChat,
  usePinAITrainerThread,
  useRenameAITrainerThread,
  useUpdateAITrainerMemory,
} from "../hooks/useAITrainer";
import { useGetCampaigns } from "@/features/campaigns/hooks/useCampaign";

const EMPTY_THREADS = [];
const TRAINER_GREETINGS = [
  "Back at it",
  "Let's train",
  "Ready when you are",
  "Let's sharpen your message",
  "What should we work on",
];
const pickTrainerGreeting = () =>
  TRAINER_GREETINGS[Math.floor(Math.random() * TRAINER_GREETINGS.length)];

const MemoryItem = ({
  memory,
  onEdit,
  onDelete,
  onRemoveChannel,
  isUpdating,
  isDeleting,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(memory.text);

  const stopEditing = () => {
    setDraft(memory.text);
    setIsEditing(false);
  };

  const save = async () => {
    const text = draft.trim();
    if (!text) return;
    if (text === memory.text.trim() || (await onEdit(memory, text))) {
      setIsEditing(false);
    }
  };

  return (
    <div className="group flex items-start gap-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 p-3 text-sm leading-6 text-gray-700 dark:text-gray-300 shadow-xs">
      {isEditing ? (
        <div className="min-w-0 flex-1 space-y-2">
          <textarea
            autoFocus
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            aria-label="Edit memory"
            className="min-h-28 w-full resize-y rounded-lg border border-slate-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm leading-6 text-gray-700 dark:text-gray-100 outline-none transition focus:border-secondary focus:ring-2 focus:ring-secondary/15"
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={stopEditing}
              disabled={isUpdating}
              className="inline-flex h-8 items-center gap-1 rounded-md px-2.5 text-xs font-medium text-slate-600 transition hover:bg-slate-200 disabled:opacity-50"
            >
              <X size={14} />
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={!draft.trim() || isUpdating}
              className="inline-flex h-8 items-center gap-1 rounded-md bg-secondary px-2.5 text-xs font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isUpdating ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Check size={14} />
              )}
              Save
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="min-w-0 flex-1">
            {memory.account_name && (
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-gray-400">
                {memory.account_name}
              </div>
            )}
            {memory.category && (
              <div className="mb-1 text-sm font-semibold text-gray-900 dark:text-gray-100">
                {memory.category}
              </div>
            )}
            {!!memory.channels?.length && (
              <div className="mb-2 flex flex-wrap gap-1">
                {memory.channels.map((channel) => (
                  <span
                    key={channel}
                    className="group/channel inline-flex items-center gap-1 rounded-full border border-secondary/20 bg-secondary-light-transparent pl-2 pr-1 py-0.5 text-[10px] font-medium text-secondary"
                  >
                    {channel.replaceAll("_", " ")}
                    <button
                      type="button"
                      aria-label={`Remove ${channel.replaceAll("_", " ")} tag`}
                      disabled={isUpdating}
                      onClick={() => onRemoveChannel(memory, channel)}
                      className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-secondary/60 opacity-0 transition hover:bg-secondary/15 hover:text-secondary group-hover/channel:opacity-100 focus-visible:opacity-100 disabled:cursor-not-allowed"
                    >
                      <X size={10} strokeWidth={2.5} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <Markdown className="markdown-content prose prose-sm dark:prose-invert max-w-none break-words [&_p]:my-1 [&_ul]:my-1 [&_li]:my-0 text-gray-800 dark:text-gray-100">
              {memory.text}
            </Markdown>
          </div>
          <CommonDropdownMenu
            ariaLabel="Memory actions"
            placement="bottom-end"
            triggerBtn={
              <button
                type="button"
                disabled={isDeleting}
                aria-label="Memory actions"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-slate-500 dark:text-gray-400 shadow-xs transition hover:border-slate-300 dark:hover:border-gray-700 hover:bg-slate-50 dark:hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/20 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isDeleting ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <EllipsisVertical size={17} />
                )}
              </button>
            }
            actions={[
              {
                id: "edit",
                name: "Edit",
                icon: <Pencil size={15} />,
                onClick: () => {
                  setDraft(memory.text);
                  setIsEditing(true);
                },
              },
              {
                id: "delete",
                name: "Delete",
                color: "danger",
                icon: <Trash2 size={15} />,
                onClick: () => onDelete(memory),
              },
            ]}
          />
        </>
      )}
    </div>
  );
};

const sortByCategory = (memories) =>
  [...memories].sort((a, b) => {
    if (!a.category && !b.category) return 0;
    if (!a.category) return 1; // uncategorized/legacy last
    if (!b.category) return -1;
    return a.category.localeCompare(b.category);
  });

const MemoryList = ({
  memories,
  emptyText,
  onEdit,
  onDelete,
  onRemoveChannel,
  updatingMemoryId,
  deletingMemoryId,
}) => (
  <div className="space-y-2 bg-slate-50 dark:bg-gray-950 p-2">
    {memories.length ? (
      sortByCategory(memories).map((memory) => (
        <MemoryItem
          key={memory.id}
          memory={memory}
          onEdit={onEdit}
          onDelete={onDelete}
          onRemoveChannel={onRemoveChannel}
          isUpdating={updatingMemoryId === memory.id}
          isDeleting={deletingMemoryId === memory.id}
        />
      ))
    ) : (
      <div className="px-3 py-4 text-sm text-slate-500 dark:text-gray-400">{emptyText}</div>
    )}
  </div>
);

const TrainedMemorySidebar = ({
  open,
  onToggle,
  data,
  campaigns,
  campaignMemories,
  campaignLearned,
  campaignMemoriesLoading,
  openCampaignId,
  setOpenCampaignId,
  userMemoryOpen,
  setUserMemoryOpen,
  accountsOpen,
  setAccountsOpen,
  onEditMemory,
  onDeleteMemory,
  onRemoveChannel,
  updatingMemoryId,
  deletingMemoryId,
}) => {
  const userMemories = data?.user_memories || [];
  const learnedPreferences = data?.learned_preferences || [];
  const accountMemories = data?.accounts?.memories || [];
  const [learnedOpen, setLearnedOpen] = useState(false);

  return (
    <aside
      className={
        open
          ? "relative h-full w-full shrink-0 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 transition-all duration-300 lg:w-[360px]"
          : "relative h-full w-12 shrink-0 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 transition-all duration-300"
      }
    >
      <Tooltip
        content="Memories"
        placement="left"
        className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-medium text-xs shadow-md border border-gray-100 dark:border-gray-700 px-3 py-1.5 rounded-lg capitalize"
      >
        <button
          type="button"
          onClick={onToggle}
          aria-label={open ? "Collapse memories" : "Open memories"}
          className="absolute left-1.5 top-1.5 z-20 flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-slate-800 dark:text-gray-100 shadow-sm transition hover:bg-slate-50 dark:hover:bg-gray-800"
        >
          <ArrowRightToLine size={21} className="text-gray-800 dark:text-gray-200" />
        </button>
      </Tooltip>

      {open && (
        <>
          <div className="flex h-12 items-center border-b border-gray-200 dark:border-gray-800 pl-14 pr-3 text-sm font-semibold text-gray-800 dark:text-gray-100">
            Memories
          </div>
          <div className="flex h-[calc(100%-48px)] flex-col gap-3 overflow-y-auto p-3">
            <section className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
              <button
                type="button"
                onClick={() => setUserMemoryOpen(!userMemoryOpen)}
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm font-semibold text-gray-900 dark:text-gray-100"
              >
                <span>Global preferences</span>
                <span className="text-xs text-slate-500 dark:text-gray-400">
                  {userMemories.length} {userMemoryOpen ? "−" : "+"}
                </span>
              </button>
              {userMemoryOpen && (
                <MemoryList
                  memories={userMemories}
                  emptyText="No global preferences yet."
                  onEdit={onEditMemory}
                  onDelete={onDeleteMemory}
                  onRemoveChannel={onRemoveChannel}
                  updatingMemoryId={updatingMemoryId}
                  deletingMemoryId={deletingMemoryId}
                />
              )}
            </section>

            <section className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
              <button
                type="button"
                onClick={() => setLearnedOpen(!learnedOpen)}
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm font-semibold text-gray-900 dark:text-gray-100"
              >
                <span>Learned from your edits</span>
                <span className="text-xs text-slate-500 dark:text-gray-400">
                  {learnedPreferences.length} {learnedOpen ? "−" : "+"}
                </span>
              </button>
              {learnedOpen && (
                <MemoryList
                  memories={learnedPreferences}
                  emptyText="Nothing learned yet. Edit an AI-written message and your style is picked up here."
                  onEdit={onEditMemory}
                  onDelete={onDeleteMemory}
                  onRemoveChannel={onRemoveChannel}
                  updatingMemoryId={updatingMemoryId}
                  deletingMemoryId={deletingMemoryId}
                />
              )}
            </section>

            <section className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
              <div className="border-b border-gray-200 dark:border-gray-800 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                Campaigns
              </div>
              <div className="space-y-2 p-2">
                {campaigns.length ? (
                  campaigns.map((campaign) => {
                    const campaignId = String(campaign.id);
                    const campaignName =
                      campaign.name ||
                      campaign.campaignName ||
                      "Untitled campaign";
                    const isOpen = openCampaignId === campaignId;
                    return (
                      <div
                        key={campaignId}
                        className="rounded-lg border border-gray-200 dark:border-gray-800"
                      >
                        <button
                          type="button"
                          onClick={() =>
                            setOpenCampaignId(isOpen ? null : campaignId)
                          }
                          className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm font-medium text-gray-800 dark:text-gray-200"
                        >
                          <span className="truncate">{campaignName}</span>
                          <span className="shrink-0 text-xs text-slate-500 dark:text-gray-400">
                            {isOpen ? "−" : "+"}
                          </span>
                        </button>
                        {isOpen && (
                          <div className="max-h-72 overflow-y-auto border-t border-gray-200 dark:border-gray-800">
                            {campaignMemoriesLoading ? (
                              <div className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                                Loading memories...
                              </div>
                            ) : (
                              <>
                                <MemoryList
                                  memories={campaignMemories}
                                  emptyText="No memories for this campaign yet."
                                  onEdit={onEditMemory}
                                  onDelete={onDeleteMemory}
                                  onRemoveChannel={onRemoveChannel}
                                  updatingMemoryId={updatingMemoryId}
                                  deletingMemoryId={deletingMemoryId}
                                />
                                {campaignLearned.length > 0 && (
                                  <>
                                    <div className="border-t border-gray-200 dark:border-gray-800 bg-slate-50 dark:bg-gray-950 px-3 pt-2 text-xs font-semibold text-slate-500 dark:text-gray-400">
                                      Learned from edits
                                    </div>
                                    <MemoryList
                                      memories={campaignLearned}
                                      emptyText=""
                                      onEdit={onEditMemory}
                                      onDelete={onDeleteMemory}
                                      onRemoveChannel={onRemoveChannel}
                                      updatingMemoryId={updatingMemoryId}
                                      deletingMemoryId={deletingMemoryId}
                                    />
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                    No campaigns yet.
                  </div>
                )}
              </div>
            </section>

            <section className="rounded-xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900">
              <button
                type="button"
                onClick={() => setAccountsOpen(!accountsOpen)}
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm font-semibold text-gray-900 dark:text-gray-100"
              >
                <span>Accounts</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {accountMemories.length} {accountsOpen ? "−" : "+"}
                </span>
              </button>
              {accountsOpen && (
                <div className="max-h-96 overflow-y-auto">
                  <MemoryList
                    memories={accountMemories}
                    emptyText="No account training yet."
                    onEdit={onEditMemory}
                    onDelete={onDeleteMemory}
                    onRemoveChannel={onRemoveChannel}
                    updatingMemoryId={updatingMemoryId}
                    deletingMemoryId={deletingMemoryId}
                  />
                </div>
              )}
            </section>
          </div>
        </>
      )}
    </aside>
  );
};

const TrainerThreadSidebar = ({
  activeThreadId,
  busy,
  isNewChat,
  onDelete,
  onNew,
  onRename,
  onSelect,
  threads,
}) => (
  <aside className="flex h-full w-[68px] shrink-0 flex-col border-r border-gray-200 dark:border-gray-800 bg-[#f7f7f5] dark:bg-gray-950 px-2 py-3 md:w-[248px] md:px-3">
    <button
      type="button"
      onClick={onNew}
      disabled={busy}
      aria-label="New chat"
      className="flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-3 text-sm font-semibold text-gray-800 dark:text-gray-100 shadow-sm transition hover:border-gray-300 dark:hover:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50 md:justify-start"
    >
      <Plus size={17} className="shrink-0" />
      <span className="hidden md:inline">New chat</span>
    </button>

    <div className="mb-2 mt-6 hidden px-2 text-[11px] font-medium text-gray-400 dark:text-gray-500 md:block">
      Recents
    </div>
    <nav className="min-h-0 flex-1 space-y-0.5 overflow-y-auto" aria-label="Recent chats">
      {/* An unsaved chat has no row yet; pin a placeholder so the list still
          shows what is open. It becomes a real thread on first send. */}
      {isNewChat && (
        <div
          aria-current="page"
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-white dark:bg-gray-900 px-2 py-2 text-left text-sm font-medium text-gray-900 dark:text-gray-100 shadow-sm md:justify-start border border-gray-200 dark:border-gray-800"
        >
          <MessageSquare size={15} className="shrink-0 md:hidden" />
          <span className="hidden min-w-0 flex-1 truncate md:block">
            New chat
          </span>
        </div>
      )}
      {threads.map((thread) => {
        const active = thread.id === activeThreadId;
        return (
          <div key={thread.id} className="group relative">
            <button
              type="button"
              onClick={() => onSelect(thread.id)}
              disabled={busy}
              aria-current={active ? "page" : undefined}
              title={thread.title}
              className={`flex w-full items-center justify-center gap-2 rounded-lg px-2 py-2 text-left text-sm transition md:justify-start md:pr-10 ${active
                ? "bg-white dark:bg-gray-900 font-medium text-gray-900 dark:text-gray-100 shadow-sm border-gray-200 dark:border-gray-800 border"
                : "text-gray-600 dark:text-gray-400 hover:bg-white/70 dark:hover:bg-gray-900/60 hover:text-gray-900 dark:hover:text-gray-100"
                }`}
            >
              <MessageSquare size={15} className="shrink-0 md:hidden" />
              <span className="hidden min-w-0 flex-1 md:block">
                <span className="block truncate">{thread.title}</span>
              </span>
            </button>
            <CommonDropdownMenu
              ariaLabel="Chat actions"
              placement="bottom-end"
              triggerBtn={
                <button
                  type="button"
                  onClick={(event) => event.stopPropagation()}
                  disabled={busy}
                  aria-label={`Actions for ${thread.title}`}
                  className="absolute right-1 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-gray-500 dark:text-gray-400 opacity-100 transition hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100 md:opacity-0 md:focus:opacity-100 md:group-hover:opacity-100"
                >
                  <EllipsisVertical size={15} />
                </button>
              }
              actions={[
                {
                  id: "rename",
                  name: "Rename",
                  icon: <Pencil size={14} />,
                  onClick: () => onRename(thread),
                },
                {
                  id: "delete",
                  name: "Delete",
                  color: "danger",
                  icon: <Trash2 size={14} />,
                  onClick: () => onDelete(thread),
                },
              ]}
            />
          </div>
        );
      })}
    </nav>
  </aside>
);

const TrainerChatHeader = ({ greeting, userName }) => (
  <div className="flex w-full flex-col items-center px-4 pt-[12vh] text-center">
    <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-2xl bg-secondary-light-transparent text-secondary dark:text-secondary-light">
      <Sparkles size={23} />
    </div>
    <h1 className="text-2xl font-medium tracking-[-0.03em] text-gray-800 dark:text-gray-100 md:text-3xl">
      {greeting}{userName ? `, ${userName.split(" ")[0]}` : ""}
    </h1>
    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
      Teach me a preference, or type @ to train me for a campaign.
    </p>
  </div>
);

const TrainerPromptInput = ({
  activeThread,
  campaigns,
  selectedCampaign,
  input,
  setInput,
  onMentionChange,
  onSend,
  disabled,
}) => {
  const mentions = activeThread?.entity_type === "campaign"
    ? campaigns.filter(
      (campaign) => String(campaign.id) === String(activeThread.entity_id),
    )
    : activeThread?.entity_type || selectedCampaign
      ? []
      : campaigns;

  return (
    <div className="mx-auto w-full max-w-3xl px-2 pb-1">
      <PromptInput
        input={input}
        setInput={setInput}
        onSend={onSend}
        disabled={disabled}
        placeholder="Teach me how you want your content written..."
        mentions={mentions}
        knownMentions={campaigns}
        onMentionChange={onMentionChange}
        hint={
          selectedCampaign
            ? "This campaign tag clears after you send"
            : "Type @ to tag a campaign"
        }
      />
    </div>
  );
};

const AITrainerMain = () => {
  const { user } = useMainStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openCampaignId, setOpenCampaignId] = useState(null);
  const [userMemoryOpen, setUserMemoryOpen] = useState(false);
  const [accountsOpen, setAccountsOpen] = useState(false);
  const [memoryPendingDelete, setMemoryPendingDelete] = useState(null);
  const [threadPendingDelete, setThreadPendingDelete] = useState(null);
  const [threadPendingRename, setThreadPendingRename] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [isNewChat, setIsNewChat] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [queuedFirstMessage, setQueuedFirstMessage] = useState(null);
  const [trainerGreeting, setTrainerGreeting] = useState(pickTrainerGreeting);
  const { data: campaignsData } = useGetCampaigns(1, 1000, "", !!user?.id);
  const campaigns = useMemo(
    () =>
      (campaignsData?.campaigns || [])
        .filter((campaign) => campaign.id != null)
        .map((campaign) => ({
          ...campaign,
          id: String(campaign.id),
          name:
            campaign.name || campaign.campaignName || "Untitled campaign",
        })),
    [campaignsData?.campaigns],
  );
  const mentionNames = useMemo(
    () => campaigns.map((campaign) => campaign.name),
    [campaigns],
  );
  const deleteMemory = useDeleteAITrainerMemory();
  const updateMemory = useUpdateAITrainerMemory();
  const {
    data: threadsData,
    isSuccess: threadsReady,
    refetch: refetchThreads,
  } = useAITrainerThreads({ userId: user?.id });
  const createThread = useCreateAITrainerThread();
  const renameThread = useRenameAITrainerThread();
  const pinThread = usePinAITrainerThread();
  const deleteThread = useDeleteAITrainerThread();
  const threads = threadsData?.threads || EMPTY_THREADS;
  const activeThread = threads.find(
    (thread) => thread.id === activeThreadId,
  );
  const threadBusy =
    createThread.isPending ||
    renameThread.isPending ||
    pinThread.isPending ||
    deleteThread.isPending;
  const handleChatComplete = useCallback(() => {
    refetchThreads();
  }, [refetchThreads]);

  const {
    messages,
    input,
    setInput,
    handleSend,
    loading,
    conversationRef,
    streamingMessageIdRef,
  } = useChatController({
    userId: user?.id,
    threadId: activeThreadId,
    fetchChatHook: useFetchAITrainerChatHistory,
    sendChatHook: useGetAITrainerChat,
    onComplete: handleChatComplete,
  });

  useEffect(() => {
    if (
      !user?.id ||
      !threadsReady ||
      isNewChat
    ) {
      return;
    }
    if (threads.some((thread) => thread.id === activeThreadId)) return;
    // The open thread went away (deleted here or elsewhere) — never silently
    // jump into someone else's conversation, fall back to a fresh chat.
    console.warn("AI Trainer active thread missing from Recents", {
      activeThreadId,
      knownThreadIds: threads.map((thread) => thread.id),
    });
    setActiveThreadId(null);
    setIsNewChat(true);
  }, [
    activeThreadId,
    isNewChat,
    threads,
    threadsReady,
    user?.id,
  ]);

  // The first message of a new chat is shown before its thread exists, so the
  // user never waits on thread creation to see what they just sent.
  const displayMessages = useMemo(
    () =>
      queuedFirstMessage
        ? [
          ...messages,
          {
            id: "queued-first-message",
            role: "user",
            content:
              queuedFirstMessage.displayText ||
              queuedFirstMessage.promptText,
          },
        ]
        : messages,
    [messages, queuedFirstMessage],
  );

  const handleNewThread = () => {
    setTrainerGreeting(pickTrainerGreeting());
    setIsNewChat(true);
    setActiveThreadId(null);
    setSelectedCampaign(null);
    setQueuedFirstMessage(null);
    setInput("");
  };

  const handleSelectThread = (threadId) => {
    setIsNewChat(false);
    setSelectedCampaign(null);
    setQueuedFirstMessage(null);
    setActiveThreadId(threadId);
  };

  const handleOpenRenameModal = (thread) => {
    if (!thread) return;
    setRenameDraft(thread.title || "");
    setThreadPendingRename(thread);
  };

  const handleSaveRename = async () => {
    if (!user?.id || !threadPendingRename) return;
    const title = renameDraft.trim();
    if (!title || title === threadPendingRename.title) {
      setThreadPendingRename(null);
      return;
    }
    try {
      await renameThread.mutateAsync({
        userId: user.id,
        threadId: threadPendingRename.id,
        title,
      });
      setThreadPendingRename(null);
    } catch {
      // Mutation toast already reports the error.
    }
  };

  const handleConfirmDeleteThread = async () => {
    if (!user?.id || !threadPendingDelete) return;
    const targetThreadId = threadPendingDelete.id;
    try {
      await deleteThread.mutateAsync({
        userId: user.id,
        threadId: targetThreadId,
      });
      setThreadPendingDelete(null);
    } catch {
      return;
    }
    if (targetThreadId !== activeThreadId) return;
    const remaining = threads.filter((item) => item.id !== targetThreadId);
    if (remaining.length) {
      setIsNewChat(false);
      setActiveThreadId(remaining[0].id);
    } else {
      setTrainerGreeting(pickTrainerGreeting());
      setActiveThreadId(null);
      setIsNewChat(true);
    }
  };

  // Stable: PromptInput reports the tag it parsed out of the typed text on
  // every change, so an unstable identity would re-run that effect each render.
  const handleCampaignMention = useCallback(
    (campaign) => {
      console.info("AI Trainer campaign mention changed", {
        campaignId: campaign?.id || null,
      });
      setSelectedCampaign(campaign);
    },
    [],
  );

  const handleTrainerSend = async (promptText = null, displayText = null) => {
    const content = promptText || input.trim();
    if (!user?.id || !content || loading || threadBusy || queuedFirstMessage) {
      return;
    }

    if (activeThreadId) {
      if (selectedCampaign && !activeThread?.entity_type) {
        try {
          await pinThread.mutateAsync({
            userId: user.id,
            threadId: activeThreadId,
            campaignId: selectedCampaign.id,
            campaignName: selectedCampaign.name,
          });
          console.info("AI Trainer thread pinned from campaign mention", {
            threadId: activeThreadId,
            campaignId: selectedCampaign.id,
          });
          setSelectedCampaign(null);
        } catch {
          return;
        }
      }
      handleSend(content, displayText);
      return;
    }

    // Render the bubble and clear the box now; the thread is created behind it.
    setQueuedFirstMessage({ promptText: content, displayText });
    setInput("");
    try {
      const data = await createThread.mutateAsync({
        userId: user.id,
        campaignId: selectedCampaign?.id,
        campaignName: selectedCampaign?.name,
      });
      console.info("AI Trainer first-message thread created", {
        threadId: data.thread.id,
      });
      // Keep fresh-chat mode active until Recents contains the new row. Otherwise
      // the reconciliation effect can reject the new ID and erase the first message.
      const refreshed = await refetchThreads();
      console.info("AI Trainer Recents refreshed after thread creation", {
        threadId: data.thread.id,
        threadVisible: !!refreshed.data?.threads?.some(
          (thread) => thread.id === data.thread.id,
        ),
      });
      setSelectedCampaign(null);
      setActiveThreadId(data.thread.id);
      setIsNewChat(false);
    } catch {
      setQueuedFirstMessage(null);
      if (!promptText) setInput(content);
    }
  };

  useEffect(() => {
    if (!queuedFirstMessage || !activeThreadId) return;
    handleSend(
      queuedFirstMessage.promptText,
      queuedFirstMessage.displayText,
    );
    setQueuedFirstMessage(null);
  }, [activeThreadId, handleSend, queuedFirstMessage]);

  const { data: trainedMemories, refetch: refetchTrainedMemories } =
    useFetchAITrainerMemories({ userId: user?.id });
  const {
    data: selectedCampaignMemories,
    isFetching: campaignMemoriesLoading,
    refetch: refetchSelectedCampaignMemories,
  } = useFetchAITrainerMemories({
    userId: user?.id,
    campaignId: openCampaignId || "",
    enabled: !!user?.id && !!openCampaignId,
  });

  useEffect(() => {
    if (!user?.id || loading) return;
    refetchTrainedMemories();
    if (openCampaignId) refetchSelectedCampaignMemories();
  }, [
    loading,
    openCampaignId,
    refetchSelectedCampaignMemories,
    refetchTrainedMemories,
    user?.id,
  ]);

  const handleDeleteMemory = () => {
    if (!user?.id || !memoryPendingDelete?.id) return;
    deleteMemory.mutate(
      {
        userId: user.id,
        memoryId: memoryPendingDelete.id,
      },
      { onSuccess: () => setMemoryPendingDelete(null) },
    );
  };

  const handleEditMemory = async (memory, text) => {
    if (!user?.id) return false;
    try {
      await updateMemory.mutateAsync({
        userId: user.id,
        memoryId: memory.id,
        text,
      });
      return true;
    } catch {
      return false;
    }
  };

  const handleRemoveMemoryChannel = async (memory, channel) => {
    if (!user?.id || !memory?.id) return;
    const channels = (memory.channels || []).filter((item) => item !== channel);
    console.info("AI Trainer memory channel removed", {
      memoryId: memory.id,
      channel,
      remainingChannels: channels,
    });
    try {
      await updateMemory.mutateAsync({
        userId: user.id,
        memoryId: memory.id,
        text: memory.text,
        channels,
      });
    } catch (error) {
      console.warn("AI Trainer memory channel removal failed", {
        memoryId: memory.id,
        channel,
        error,
      });
    }
  };

  return (
    <div className="relative flex h-[calc(100vh-41px)] w-full overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950">
      <TrainerThreadSidebar
        activeThreadId={activeThreadId}
        busy={threadBusy || loading}
        isNewChat={isNewChat}
        onDelete={(thread) => setThreadPendingDelete(thread)}
        onNew={handleNewThread}
        onRename={handleOpenRenameModal}
        onSelect={handleSelectThread}
        threads={threads}
      />
      <div className="min-w-0 flex-1 bg-[#fcfbf9] dark:bg-gray-900/50 p-2">
        <div className="h-full min-h-0 overflow-hidden rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800">
          <BaseChat
            messages={displayMessages}
            input={input}
            setInput={setInput}
            onSend={handleTrainerSend}
            loading={loading}
            conversationRef={conversationRef}
            streamingMessageIdRef={streamingMessageIdRef}
            className="!rounded-none !border-0 !bg-transparent !p-0"
            contentClassName="mx-auto w-full max-w-4xl px-3 pt-0 md:px-8"
            Header={displayMessages.length ? null : TrainerChatHeader}
            headerProps={{ greeting: trainerGreeting, userName: user?.name }}
            Message={ChatMessage}
            messageProps={{ mentionNames }}
            Input={TrainerPromptInput}
            inputDisabled={
              loading || threadBusy || !!queuedFirstMessage
            }
            inputProps={{
              activeThread,
              campaigns,
              selectedCampaign,
              onMentionChange: handleCampaignMention,
            }}
            userImage={user?.profileImage}
            userName={user?.name}
          />
        </div>
      </div>
      <div className="py-2 pr-2">
        <TrainedMemorySidebar
          open={sidebarOpen}
          onToggle={() => setSidebarOpen((value) => !value)}
          data={trainedMemories}
          campaigns={campaigns}
          campaignMemories={
            selectedCampaignMemories?.campaigns?.[0]?.memories || []
          }
          campaignLearned={
            selectedCampaignMemories?.campaigns?.[0]?.learned || []
          }
          campaignMemoriesLoading={campaignMemoriesLoading}
          openCampaignId={openCampaignId}
          setOpenCampaignId={setOpenCampaignId}
          userMemoryOpen={userMemoryOpen}
          setUserMemoryOpen={setUserMemoryOpen}
          accountsOpen={accountsOpen}
          setAccountsOpen={setAccountsOpen}
          onEditMemory={handleEditMemory}
          onDeleteMemory={setMemoryPendingDelete}
          onRemoveChannel={handleRemoveMemoryChannel}
          updatingMemoryId={
            updateMemory.isPending ? updateMemory.variables?.memoryId : null
          }
          deletingMemoryId={
            deleteMemory.isPending ? memoryPendingDelete?.id : null
          }
        />
      </div>
      {memoryPendingDelete && (
        <DeleteConfirmationModal
          showConfirmDelete={!!memoryPendingDelete}
          onClose={() => {
            if (!deleteMemory.isPending) setMemoryPendingDelete(null);
          }}
          title="Delete this memory?"
          description="This memory will be permanently deleted and will no longer guide generated content."
          onDelete={handleDeleteMemory}
          deleteButtonText="Delete memory"
          cancelButtonText="Cancel"
          isDeleting={deleteMemory.isPending}
        />
      )}
      {threadPendingDelete && (
        <DeleteConfirmationModal
          showConfirmDelete={!!threadPendingDelete}
          onClose={() => {
            if (!deleteThread.isPending) setThreadPendingDelete(null);
          }}
          title={`Delete "${threadPendingDelete.title}"?`}
          description="This conversation will be permanently deleted - This action can’t be undone."
          onDelete={handleConfirmDeleteThread}
          deleteButtonText="Delete chat"
          cancelButtonText="Cancel"
          isDeleting={deleteThread.isPending}
        />
      )}
      {threadPendingRename && (
        <CustomModal
          isOpen={!!threadPendingRename}
          onClose={() => {
            if (!renameThread.isPending) setThreadPendingRename(null);
          }}
          width="400px"
        >
          <div className="flex flex-col gap-4 w-full">
            <div className="flex items-center justify-between">
              <h3 className="text-gray-900 font-bold text-base">Rename chat</h3>
              <button
                type="button"
                className="p-1 hover:bg-gray-100 rounded-full text-gray-500 transition-colors"
                onClick={() => setThreadPendingRename(null)}
              >
                <X size={18} />
              </button>
            </div>
            <CommonInput
              autoFocus
              value={renameDraft}
              onChange={(e) => setRenameDraft(e.target.value)}
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" &&
                  renameDraft.trim() &&
                  !renameThread.isPending
                ) {
                  handleSaveRename();
                }
              }}
              placeholder="Enter new chat title"
              disabled={renameThread.isPending}
            />
            <div className="flex justify-end gap-2.5">
              <SecondaryButton
                textColor="dark"
                title="Cancel"
                onClick={() => setThreadPendingRename(null)}
                disabled={renameThread.isPending}
              />
              <PrimaryButton
                startContent={false}
                onClick={handleSaveRename}
                disabled={!renameDraft.trim() || renameThread.isPending}
              >
                {renameThread.isPending ? "Saving..." : "Save"}
              </PrimaryButton>
            </div>
          </div>
        </CustomModal>
      )}
    </div>
  );
};

export default AITrainerMain;
